import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  CreditCard as CreditCardIcon,
  Cancel as CancelIcon,
  RestartAlt as RestartAltIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFilmSubscription } from '../../hooks/useFilmSubscription';
import {
  FILM_TIER_NAMES,
  FILM_TIERS,
} from '../../lib/filmSubscriptionTiers';
import {
  createCheckoutSession,
  createPortalSession,
  cancelSubscriptionAtPeriodEnd,
  resumeSubscription,
} from '../../lib/services/stripeService';
import FilmPricingTable from '../../components/Subscription/FilmPricingTable';
import { brandColors } from '../../theme/theme';

const formatDate = (value) => {
  if (!value) return 'N/A';

  const date = value.toDate ? value.toDate() : new Date(value);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const {
    currentUser,
    userProfile,
    refreshUserProfile,
  } = useAuth();
  const {
    filmTier,
    filmStatus,
    filmStartedAt,
    filmExpiresAt,
    filmCancelsAtPeriodEnd,
    isEnterpriseMember,
    isFilmEnterpriseAdmin,
  } = useFilmSubscription();

  const [upgradeTier, setUpgradeTier] = useState(null);
  const [actionLoading, setActionLoading] = useState({
    cancel: false,
    resume: false,
    portal: false,
  });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const fullName = useMemo(() => {
    if (userProfile?.displayName) return userProfile.displayName;
    const first = userProfile?.firstName || '';
    const last = userProfile?.lastName || '';
    const combined = `${first} ${last}`.trim();
    return combined || currentUser?.displayName || 'User';
  }, [userProfile, currentUser]);

  const currentTierName = FILM_TIER_NAMES[filmTier] || 'Film Free';
  const statusLabel = filmStatus
    ? filmStatus.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
    : 'Unknown';

  const handleUpgrade = async (tier) => {
    try {
      setError(null);
      setMessage(null);
      setUpgradeTier(tier);

      const origin = window?.location?.origin || 'https://app.greenlightanalytics.com';
      const successUrl = `${origin}/profile?status=success&tier=${tier}`;
      const cancelUrl = `${origin}/profile?status=cancelled`;

      const checkoutUrl = await createCheckoutSession(
        currentUser?.uid,
        tier,
        'monthly',
        {
          successUrl,
          cancelUrl,
        },
      );

      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
      }
    } catch (err) {
      console.error('Error starting upgrade checkout:', err);
      setError(err?.message || 'Unable to start checkout. Please try again.');
    } finally {
      setUpgradeTier(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setError(null);
      setMessage(null);
      setActionLoading((prev) => ({ ...prev, cancel: true }));
      await cancelSubscriptionAtPeriodEnd();
      setMessage('Your subscription will end at the close of the current billing period.');
      await refreshUserProfile();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      setError(err?.message || 'Unable to cancel subscription. Please try again.');
    } finally {
      setActionLoading((prev) => ({ ...prev, cancel: false }));
    }
  };

  const handleResumeSubscription = async () => {
    try {
      setError(null);
      setMessage(null);
      setActionLoading((prev) => ({ ...prev, resume: true }));
      await resumeSubscription();
      setMessage('Your subscription will continue without interruption.');
      await refreshUserProfile();
    } catch (err) {
      console.error('Error resuming subscription:', err);
      setError(err?.message || 'Unable to resume subscription. Please try again.');
    } finally {
      setActionLoading((prev) => ({ ...prev, resume: false }));
    }
  };

  const handleOpenBillingPortal = async () => {
    try {
      setError(null);
      setMessage(null);
      setActionLoading((prev) => ({ ...prev, portal: true }));

      const origin = window?.location?.origin || 'https://app.greenlightanalytics.com';
      const portalUrl = await createPortalSession(`${origin}/profile`);

      window.location.assign(portalUrl);
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError(err?.message || 'Unable to open billing portal. Please try again.');
    } finally {
      setActionLoading((prev) => ({ ...prev, portal: false }));
    }
  };

  const renderEnterpriseNotice = () => {
    if (!isEnterpriseMember) return null;

    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Your Film subscription is managed by your company.
        {isFilmEnterpriseAdmin
          ? ' You can manage seats and assignments from the company dashboard.'
          : ' Please contact your company administrator to make changes.'}
      </Alert>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Profile & Subscription
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your account details and Film subscription preferences.
        </Typography>
        {message && (
          <Alert severity="success" onClose={() => setMessage(null)}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Account Information
              </Typography>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {fullName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentUser?.email || 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Role
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {userProfile?.role || 'user'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                sx={{ alignSelf: 'flex-start' }}
                disabled
              >
                Edit Profile (Coming Soon)
              </Button>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Film Subscription
              </Typography>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={currentTierName}
                  color="primary"
                  sx={{
                    bgcolor: brandColors.primary,
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={statusLabel}
                  color={filmStatus === 'active' ? 'success' : 'default'}
                  sx={{ fontWeight: 600 }}
                />
                {filmCancelsAtPeriodEnd && (
                  <Chip
                    label="Cancels at period end"
                    color="warning"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Stack>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Subscription Start
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(filmStartedAt)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Current Period Ends
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {formatDate(filmExpiresAt)}
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={<CreditCardIcon />}
                  onClick={() => navigate('/subscription/film')}
                >
                  Review Plans
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LaunchIcon />}
                  onClick={handleOpenBillingPortal}
                  disabled={actionLoading.portal}
                >
                  {actionLoading.portal ? 'Opening…' : 'Billing Portal'}
                </Button>
              </Stack>

              {!isEnterpriseMember && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelSubscription}
                  disabled={
                    actionLoading.cancel
                    || filmCancelsAtPeriodEnd
                    || filmTier === FILM_TIERS.FREE
                  }
                  >
                    {actionLoading.cancel ? 'Processing…' : 'Cancel at Period End'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<RestartAltIcon />}
                    onClick={handleResumeSubscription}
                  disabled={
                    actionLoading.resume
                    || !filmCancelsAtPeriodEnd
                    || filmTier === FILM_TIERS.FREE
                  }
                  >
                    {actionLoading.resume ? 'Processing…' : 'Keep Subscription'}
                  </Button>
                </Stack>
              )}

              {renderEnterpriseNotice()}
            </Stack>
          </Paper>
        </Grid>

        {!isEnterpriseMember && (
          <Grid item xs={12}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Explore Film Plans
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upgrade to unlock additional analytics, film detail insights, and enterprise collaboration tools.
                </Typography>
                <FilmPricingTable
                  currentTier={filmTier}
                  onUpgrade={handleUpgrade}
                  processingTier={upgradeTier}
                />
              </Stack>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ProfilePage;


